import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
// @mui
import {
  Stack,
  Typography,
  Button,
  Link,
} from "@mui/material";
import { LoadingButton } from '@mui/lab';
import { styled } from '@mui/material/styles';
// hooks
import useResponsive from '../../../../hooks/useResponsive';
import useAuth from '../../../../hooks/useAuth';
// form
import { useForm } from 'react-hook-form';
// components
import DIOText from '../../../../components/DIOText';
import Iconify from '../../../../components/Iconify';
import Label from '../../../../components/Label';
import SvgIconStyle from '../../../../components/SvgIconStyle';
import {
  PositionLabel,
  JobPostFiles,
  CrewRejectHistoryDialog,
} from "../../";
import {
  PositionGuideDialog,
  HowToWriteDialog,
  JobPostHelpDialog,
  SpeedMatchingDialog,
} from '../';
import {
  FormProvider,
  RHFTextField,
  RHFCheckbox,
} from '../../../../components/hook-form';
// util
import axios from '../../../../utils/axios';
import {default as axiosNormal} from 'axios';
// config
import { EVENT_ID } from '../../../../config';

const Spacer = styled('div')(({ theme }) => {
  return {
    marginBottom: theme.spacing(5),
  };
});


export default function SetTaskForm({ jobPost, next, isEdit=false, toggleDialog }) {
  const { user } = useAuth();
  const smUp = useResponsive('up', 'sm');
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmit, setIsSubmit] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);
  const [isOpenMeetingDialog, setIsOpenMeetingDialog] = useState(false);
  const [isOpenHowToWrite, setIsOpenHowToWrite] = useState(false);

  // dialogs
  const [isOpenPositionGuide, setIsOpenPositionGuide] = useState(false);
  const [crewRejectHistoryOpen, setCrewRejectHistoryOpen] = useState(false);
  const [speedMatchingOpen, setSpeedMatchingOpen] = useState(false);

  const noSamplePositions = ["finance", "operator", "engineer"].includes(jobPost?.position);

  const defaultValues = {
    role: jobPost?.detail_role?.[0] || [],
    jobIntro: jobPost?.intro_to_crew || `${user?.employee.company[0].service_detail||''}` || '',
    taskFor4weeks: jobPost?.task_for_4weeks || '',
    files: jobPost?.files || [],
    agreeSpeedMatching: !!jobPost?.agree_to_open || false,
  }

  const methods = useForm({
    defaultValues: defaultValues,
  });

  const { handleSubmit, watch, setError, getValues } = methods;

  const watchValues = async (value, name, type) => {
    setCanSubmit(!!value.jobIntro && !!value.taskFor4weeks);
  }

  useEffect(() => {
    const initialize = async () => {
      try {
        const value = getValues();
        setCanSubmit(!!value.jobIntro && !!value.taskFor4weeks);
      } catch (err) {
        console.error(err);
      }
    };
    initialize();

    const subscription = watch((value, { name, type }) => watchValues(value, name, type ));
    return () => subscription.unsubscribe();
  }, [watch]);

  const openFile = async (item) => {
    const companyId = user?.employee.company[0].id;
    const jobPostId = jobPost?.id;
    const response = await axios.post(`/api/company/${companyId}/job-posts/${jobPostId}/files/${item.id}/url/`);
    window.open(response.data.url);
  };

  const fileUploadBeforeSubmit = async (files) => {
    const fileNameList = [];
    const companyId = user?.employee.company[0].id;
    for (let f of files) {
      if (f.id && f.name) {
        fileNameList.push({ id: f.id, file: f.name, name: f.name })
      } else {
        if (f.file === '') continue;
        const urlResponse = await axios.post(
          `/api/company/${companyId}/job-posts/file-url/`,
          {filename: f.file.name}
        );
        const {filename, url} = urlResponse.data;

        // upload file to s3 via presigned url
        await axiosNormal.put(
          url,
          f.file,
          {
            headers: {
              'Content-Type': f.file.type
            }
          }
        );
        fileNameList.push({ file: filename, name: f.file.name });
      }
    }
    return fileNameList;
  };

  const makeJobPostData = async (data) => {
    const { role, taskFor4weeks, files, jobIntro, agreeSpeedMatching } = data;
    const fileUploadedData = await fileUploadBeforeSubmit(files);
    return {
      detail_role: role?.length ? role[0].id : null,
      intro_to_crew: jobIntro,
      task_for_4weeks: taskFor4weeks,
      files: fileUploadedData,
      agree_to_open: agreeSpeedMatching,
    }
  };

  const handleError = (error) => {
    console.error(error);
    if (error.response && error.response.status === 400) {
      setError('afterSubmit', { type: "manual", message: error.response.data.message});
    } else {
      setError('afterSubmit', { type: "manual", message: "저장에 실패하였습니다. 관리자에 문의해주세요."});
    }
  };

  const searchRequest = async (data) => {
    setIsSubmit(true);
    const companyId = user?.employee.company[0].id;
    try {
      const jobPostData = await makeJobPostData(data);
      const jobPostId = jobPost.id;
      await axios.post(`/api/company/${companyId}/job-posts/${jobPostId}/task/`, jobPostData);
      if (smUp) {
        enqueueSnackbar('저장되었습니다');
      } else {
        enqueueSnackbar('저장되었습니다', {anchorOrigin: { vertical: 'bottom', horizontal: 'center' }});
      }
      if (isEdit) await toggleDialog(jobPostId);
      else await next(jobPostId);
      setIsSubmit(false);
    } catch (error) {
      setIsSubmit(false);
      handleError(error);
    }
  }

  const openSetMeetingDialog = () => {
    setIsOpenMeetingDialog(true);
  };

  return (
    <>
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      {/* # Label */}
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
        <Label variant="outlined" color="secondary" sx={{ borderRadius: 2}}>
          #원격 근무
        </Label>
        <Label variant="outlined" color="secondary" sx={{ borderRadius: 2}}>
          #파트타임
        </Label>
        <Label variant="outlined" color="secondary" sx={{ borderRadius: 2}}>
          #4주 단위
        </Label>
      </Stack>
      {/* help button in smUp */}
      <Stack direction="row" alignItems="center" spacing={1}>
        {smUp && !isEdit && (
        <Button
          variant="text"
          color="error"
          startIcon={<SvgIconStyle src={`/icons/ic_headphone.svg`} sx={{ width: 28, height: 28, color: 'error.main' }} />}
          onClick={openSetMeetingDialog}
        >
          공고 작성이 어려우신가요?
        </Button>
        )}
        {smUp && isEdit && jobPost.crew_reject_count > 0 && (
        <Button
          variant="text"
          color="error"
          startIcon={<Iconify icon={"eva:alert-triangle-fill"} sx={{ width: 28, height: 28, color: 'error.main' }} />}
          onClick={() => setCrewRejectHistoryOpen(true)}
          disableRipple
        >
          {jobPost.crew_reject_count}명의 매칭 거절 사유 확인하기
        </Button>
        )}
      </Stack>
    </Stack>
    <Stack direction="row" alignItems="center" sx={{ mb: 1 , flexWrap: 'wrap', gap: smUp ? 0.9 : 0.5 }}>
      <PositionLabel position={jobPost?.position} detailRole={jobPost?.detail_role} aiSelected={jobPost?.select_ai_position} />
      <Typography variant="h4">
        에게 전달할 업무를
      </Typography>
      <Typography variant="h4">
        자세히 알려주세요.
      </Typography>
    </Stack>

    <Typography variant="body2" sx={{ mb: { xs: 3 , sm: 6 }, ...!smUp && { color: 'text.secondary' } }}>
      자세한 요청 업무를 작성해주시면, 정확하고 빠른 매칭이 가능합니다.
    </Typography>

    <FormProvider methods={methods}>
      <Typography variant="subtitle1" gutterBottom>
        회사/서비스 소개
      </Typography>
      <RHFTextField
        multiline
        minRows={1}
        name="jobIntro"
        placeholder="예) www.dio.so - 사이드잡 매칭 플랫폼"
      />

      <Spacer />

      <Typography variant="subtitle1" gutterBottom>
        전달할 업무 내용
      </Typography>
      <Typography variant="body2" gutterBottom={noSamplePositions}>
        작성하신 내용은 <DIOText />에서 검토한 후 다시 연락드립니다. 편하게 작성해 주세요.
      </Typography>

      {!noSamplePositions && (
      <Button
        size={smUp ? "medium" : "small"}
        sx={{
          color: 'info.main',
          mb: 0.5,
        }}
        startIcon={<Iconify icon={'eva:alert-circle-fill'} />}
        onClick={() => setIsOpenHowToWrite(true)}
      >
        업무 내용을 보통 어떻게 작성하나요?
      </Button>
      )}

      <RHFTextField
        multiline
        minRows={5}
        name="taskFor4weeks"
        placeholder={"크루에게 요청할 업무 내용 (최소 30자)"}
      />

      <Typography variant="subtitle1" sx={{ mt: 5, mb: { xs: 0, sm: 1 } }}>
        참고해야 할 자료가 있다면 업로드해주세요.
      </Typography>
      <JobPostFiles openFile={openFile} />

      {/* speed matching */}
      <Typography variant="subtitle1" sx={{ mt: 5 }}>
        [스피드매칭] 2~3배 더 빠른 매칭을 희망하시나요?
      </Typography>
      <Button
        size={smUp ? "medium" : "small"}
        sx={{
          color: 'info.main',
          mt: 0.5,
          mb: 1.5,
        }}
        startIcon={<Iconify icon={'eva:alert-circle-fill'} />}
        onClick={() => setSpeedMatchingOpen(true)}
      >
        스피드 매칭은 어떤 방식으로 진행되나요?
      </Button>
      <Stack direction="row" alignItems={"center"} justifyContent={"space-between"}
        sx={{
          border: (theme) => `solid 1px ${theme.palette.divider}`,
          borderRadius: 1,
          px: 2,
          py: 0.5,
          "&:hover": {
            backgroundColor: (theme) => theme.palette.action.hover,
          },
          borderColor: watch("agreeSpeedMatching") ? 'primary.main' : 'divider',
        }}
      >
        <RHFCheckbox
          name="agreeSpeedMatching"
          label="네. 스피드매칭 진행을 희망합니다."
        />
      </Stack>

    </FormProvider>

    <LoadingButton
      id={EVENT_ID.bizJobPostSearchRequest}
      size="large"
      variant="contained"
      sx={{ mt: smUp ? 7 : 6 }}
      disabled={!canSubmit}
      onClick={handleSubmit(searchRequest)}
      loading={isSubmit}
    >
      완료
    </LoadingButton>

    <PositionGuideDialog
      role={getValues().role}
      isOpen={isOpenPositionGuide}
      closeDialog={() => setIsOpenPositionGuide(false)}
    />

    <HowToWriteDialog
      open={isOpenHowToWrite}
      closeDialog={() => setIsOpenHowToWrite(false)}
      position={jobPost?.position}
    />

    <JobPostHelpDialog
      jobPostId={jobPost?.id}
      open={isOpenMeetingDialog}
      closeDialog={() => setIsOpenMeetingDialog(false)}
    />

    <CrewRejectHistoryDialog
      isOpen={crewRejectHistoryOpen}
      closeDialog={() => setCrewRejectHistoryOpen(false)}
      history={jobPost?.candidate_set}
      editJobPost={null}
    />

    <SpeedMatchingDialog
      isOpen={speedMatchingOpen}
      closeDialog={() => setSpeedMatchingOpen(false)}
    />
    </>
  )
}